import React, { useMemo, useRef } from "react";
import { useSecondaryToolbar } from "../../../Hooks/useSecondaryToolbar";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import {
  selectIntroPopupActive,
  selectSessionExpired,
  selectShowGameAfterTransition,
} from "../../../State/selectors";
import Settings from "./Settings/Settings";
import BuyBonusPopup from "./Popups/BuyBonusPopup";
import ToolbarTop from "./Toolbars/ToolbarTop";
import ToolbarBottom from "./Toolbars/ToolbarBottom";
import BetSelectPopup from "./Popups/BetSelectPopup";
import AutoplayPopup from "./Popups/AutoplayPopup";
import WarningPopup from "./Popups/WarningPopup";
import InfoPopup from "./Popups/InfoPopup";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import DevPanel from "../shared/DevPanel/DevPanel";
import { ResizeProbe } from "../../../utils/resizeProfiler";
import { DimensionsContext, hudDimensions, useDimensions } from "../../../Contexts/DimensionsContext";
// import ConnectionPopup from "../shared/Popups/ConnectionPopup";

// ── The HUD in one 16:9 block (FEATURES_CONFIG.LAYOUT.NO_TABLET) ── in a
// frame narrower than 16:9 the toolbars, buttons and the popups that hang off
// them are laid out for a 16:9 box of the frame's width and moved down to it,
// centred — they do not spread to the frame's edges (like 3 Pots of Destiny).
// Their own context sees the box as the screen. The game, the background and
// the full-screen popups (Settings, Buy Bonus, Warning, Info) keep the whole
// frame. Switch off: the box is the frame, nothing moves.
const UserInterface = ({ children }) => {
  const dimensionsContext = useDimensions();
  const { dimensions } = dimensionsContext;
  const hudContext = useMemo(() => hudDimensions(dimensionsContext), [dimensionsContext]);
  const boxed = hudContext !== dimensionsContext;

  return (
    <>
      <DimensionsContext.Provider value={hudContext}>
        {/* <Hud offsetY={boxed ? dimensions.uiBoxOffsetY : 0}>{children}</Hud> */}
        {/* ...and across, into the upright 9:16 column (an iPad, 2026-10-29). */}
        <Hud
          offsetX={boxed ? dimensions.uiBoxOffsetX || 0 : 0}
          offsetY={boxed ? dimensions.uiBoxOffsetY : 0}
        >
          {children}
        </Hud>
      </DimensionsContext.Provider>

      {/* Settings: full-frame like Autoplay looks, its popup still above the
          HUD toolbar (Settings reads the HUD block). */}
      <ResizeProbe id="UI · Settings" nested>
        <Settings />
      </ResizeProbe>

      {/* Render popups OUTSIDE the main container so they're always visible */}
      {FEATURES_CONFIG.BONUS_BUY.ENABLED && (
        <ResizeProbe id="UI · BuyBonusPopup" nested>
          <BuyBonusPopup />
        </ResizeProbe>
      )}
      <ResizeProbe id="UI · WarningPopup" nested>
        <WarningPopup />
      </ResizeProbe>
      <ResizeProbe id="UI · InfoPopup" nested>
        <InfoPopup />
      </ResizeProbe>

      {/* Dev panel — test trigger buttons (top-left corner) */}
      {FEATURES_CONFIG.DEV_TOOLS.DEV_PANEL && <DevPanel />}
    </>
  );
};

// const UserInterface = ({ children }) => {
const Hud = ({ children, offsetX = 0, offsetY }) => {
  const toolbar = useSecondaryToolbar();
  const containerRef = useRef(null);
  const introPopupActive = useSelector(selectIntroPopupActive);
  const sessionExpired = useSelector(selectSessionExpired);
  const showGameAfterTransition = useSelector(selectShowGameAfterTransition);

  const getInterfaceAlpha = () => {
    // // Dev: the whole UI invisible but in place (DEV_TOOLS.HIDE_UI).
    // if (FEATURES_CONFIG.DEV_TOOLS.HIDE_UI) return 0.001;
    // (Only the two toolbars now — see ToolbarTop / ToolbarBottom.)
    if (showGameAfterTransition) {
      // Game interface should be fully visible after transition
      return 1;
    } else if (introPopupActive || sessionExpired) {
      // Game interface should be hidden when intro popup or reload dialog is active
      return 0.001;
    } else {
      // Default state - game interface visible
      return 1;
    }
  };

  const interfaceAlpha = getInterfaceAlpha();

  return (
    <>
      <Container
        ref={containerRef}
        name="secondary_user_interface"
        sortableChildren
        alpha={interfaceAlpha}
        x={offsetX}
        y={offsetY}
      >
        <ResizeProbe id="UI · ToolbarTop" nested>
          <ToolbarTop />
        </ResizeProbe>
        {children}
        <ResizeProbe id="UI · ToolbarBottom" nested>
          <ToolbarBottom />
        </ResizeProbe>

        {/* <ResizeProbe id="UI · Settings" nested>
          <Settings />
        </ResizeProbe> — full-frame now, see UserInterface */}
        <ResizeProbe id="UI · BetSelectPopup" nested>
          <BetSelectPopup toolbar={toolbar} />
        </ResizeProbe>
        <ResizeProbe id="UI · AutoplayPopup" nested>
          <AutoplayPopup toolbar={toolbar} />
        </ResizeProbe>

        {/* <ConnectionPopup /> */}
      </Container>

      {/* Moved to UserInterface (outside the HUD's 16:9 block):
      {FEATURES_CONFIG.BONUS_BUY.ENABLED && (
        <ResizeProbe id="UI · BuyBonusPopup" nested>
          <BuyBonusPopup />
        </ResizeProbe>
      )}
      <ResizeProbe id="UI · WarningPopup" nested>
        <WarningPopup />
      </ResizeProbe>
      <ResizeProbe id="UI · InfoPopup" nested>
        <InfoPopup />
      </ResizeProbe>
      {FEATURES_CONFIG.DEV_TOOLS.DEV_PANEL && <DevPanel />} */}
    </>
  );
};

export default UserInterface;
